import fetchWithTimeout from '../../helpers/__base/fetchWithTimeout';
import sleep from '../../helpers/__base/sleep';
import {
  IBaseApiService,
  IBaseApiServiceParams,
  IError,
  IFetchDataObjectBase,
  IFetchOptions,
  InterceptorRequest,
  InterceptorResponse,
  Mode,
  RequestMethod,
} from '../../types/index.d';
import errors from './errors.json';

const settings = {
  basePath: '',
  className: '',
  error: false,
  mode: Mode.Production,
};

/* TODO: copyFetchOptions => mergeFetchOptions */
const copyFetchOptions = (options: IFetchOptions) => {
  const optionsCopy: IFetchOptions = {};

  for (const o in options) {
    if (o === 'headers') continue;
    optionsCopy[o] = options[o];
  }

  const headers = options.headers;
  if (headers) {
    const headersCopy: IFetchOptions['headers'] = {};

    for (const f in options) {
      headersCopy[f] = headers[f];
    }

    optionsCopy.headers = headersCopy;
  }

  return optionsCopy;
};

export const responseInterceptor: InterceptorResponse = async ({
  data,
  response,
}) => {
  if (response.bodyUsed) return data;

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) return data;

  let dataProcessed = await response.json();
  const dataResult = dataProcessed?.data?.result;
  if (dataResult) dataProcessed = dataResult;

  return dataProcessed;
};

export default class BaseApiService implements IBaseApiService {
  static interceptors: {
    req: InterceptorRequest[];
    res: InterceptorResponse[];
  } = {
    req: [],
    res: [responseInterceptor],
  };

  basePath: string;
  className: string;
  error: boolean;
  mode: Mode;

  ERRORS: Record<string, string>;

  constructor(params: IBaseApiServiceParams = settings) {
    this.basePath = params.basePath || settings.basePath;
    this.className = params.className || this.constructor.name;
    this.error = params.error || settings.error;
    this.mode = params.mode || settings.mode;

    this.ERRORS = errors;

    if (this.mode === Mode.Development) {
      // _eslint-disable-next-line prettier/prettier
      this._log(
        '%c' + Mode.Development + ' mode',
        'background: yellow; color: #000;'
      );
    }
  }

  static register(cb: (options: Record<string, any>) => any, isRes?: boolean) {
    BaseApiService.interceptors[isRes ? 'res' : 'req'].push(cb);
  }

  static unregister(cb: (options: Record<string, any>) => any): boolean {
    const interceptors = BaseApiService.interceptors;

    let cbIndex = interceptors.req.findIndex(i => i === cb);
    if (cbIndex >= 0) {
      interceptors.req.splice(cbIndex, 1);
      return true;
    }

    cbIndex = interceptors.res.findIndex(i => i === cb);
    if (cbIndex >= 0) {
      interceptors.res.splice(cbIndex, 1);
      return true;
    }

    return false;
  }

  jsonStringify(data: any) {
    return JSON.stringify(data);
  }

  async sendRequest(
    path: string,
    params = {},
    options: Record<string, any> = {},
    {
      data,
      delay = 10,
      method,
      options: reqFetchOptions,
      timeout,
    }: IFetchDataObjectBase = {
      data: undefined,
      method: undefined,
      timeout: 10000,
    }
  ) {
    let fetchResponse;
    let fetchResponseData;

    // console.log('sendRequest', path);

    try {
      if (this.mode === Mode.Production) {
        /* Options */
        let fetchOptions = options ? copyFetchOptions(options) : {};
        fetchOptions.headers = {
          ...(fetchOptions.headers || {}),
        };

        if (fetchOptions.body) {
          // TODO: Add body check

          //
          if (
            fetchOptions.method === RequestMethod.POST ||
            fetchOptions.method === RequestMethod.PUT
          ) {
            const fetchHeaders = fetchOptions.headers;

            if (fetchHeaders['Content-Type']) {
              if (fetchHeaders['Content-Type'].indexOf('application/json') < 0)
                fetchHeaders['Content-Type'] += '; application/json';
            } else {
              fetchHeaders['Content-Type'] = 'application/json';
            }
          }

          if (options.body instanceof FormData) {
            fetchOptions.body = options.body;
            delete fetchOptions.headers['Content-Type'];
          }
        }

        /* Params */
        const queryParams = params
          ? new URLSearchParams(params).toString()
          : '';
        const url =
          this.basePath + path + (queryParams.length ? '?' + queryParams : '');

        /* Request interceptors */
        let interceptors: any[] = BaseApiService.interceptors.req;

        const fetchOptionsPure = copyFetchOptions(fetchOptions);
        if (interceptors.length) {
          for (const interceptor of interceptors) {
            fetchOptions = await interceptor({
              method,
              options: fetchOptions,
              optionsPure: fetchOptionsPure,
              url,
            });
          }
        }

        /*  */
        if (reqFetchOptions) {
          /* TODO: mergeFetchOptions */
          for (const f in reqFetchOptions) {
            if (f === 'headers') continue;
            fetchOptions[f] = reqFetchOptions[f];
          }

          const fHeaders = fetchOptions.headers;
          const rHeaders = reqFetchOptions.headers;
          if (rHeaders) {
            for (const f in rHeaders) {
              fHeaders[f] = rHeaders[f];
            }
          }
        }

        /* Fetching */
        // this._log('url', url);
        // this._log('fetchOptions', fetchOptions);
        const fetchFunc = timeout ? fetchWithTimeout : fetch;

        /* Processing response */
        fetchResponse = await fetchFunc(url, fetchOptions);

        /* Response interceptors */
        interceptors = BaseApiService.interceptors.res;
        if (interceptors.length) {
          for (const interceptor of interceptors) {
            fetchResponseData = await interceptor({
              data: fetchResponseData,
              method,
              response: fetchResponse,
              url,
            });
          }
        }

        /*  */
        if (fetchResponse.status >= 500) {
          throw new Error(this.ERRORS['2']);
        }

        /*  */
        if (!fetchResponse.bodyUsed) {
          try {
            const contentType = fetchResponse.headers.get('content-type');

            if (contentType) {
              if (
                contentType.includes('application/json') ||
                contentType.includes('application/v2+json') ||
                contentType.includes('text/plain')
              ) {
                fetchResponseData = await fetchResponse.json();

                if (
                  fetchResponseData &&
                  typeof fetchResponseData === 'string'
                ) {
                  fetchResponseData = JSON.parse(fetchResponseData);
                }
              }
            }
          } catch (error) {
            this._logWarn({
              error,
              method: method || 'sendRequest',
            });
          }
        }

        return {
          _response: fetchResponse,
          errors: [],
          response: this._getMergeData(
            this._processResponseData(fetchResponseData, method),
            method
          ),
          status: true,
        };
      } else {
        await sleep(delay);

        const dataToUse = data ?? this.data;

        return this.error
          ? {
              _response: fetchResponse,
              errors: [this._getErrorDescription('0')],
              response: null,
              status: false,
            }
          : {
              _response: fetchResponse,
              errors: [],
              response: this._getMergeData(dataToUse, method),
              status: true,
            };
      }
    } catch (error) {
      this._logError({ error, method });

      let data;
      if (fetchResponse) {
        try {
          if (!fetchResponse.bodyUsed) {
            data = await fetchResponse.json();
          } else {
            data = fetchResponseData;
          }

          if (typeof data === 'string' && data) {
            data = JSON.parse(data);
          }
        } catch (error) {
          this._logWarn({
            error,
            method: method || 'sendRequest',
          });
        }
      }

      return {
        _data: data,
        _response: fetchResponse,
        errors: [{ code: null, message: error.message }],
        response: null,
        status: false,
      };
    }
  }

  /* Protected */
  _getErrorDescription(code: string): IError {
    return {
      code,
      message: this.ERRORS[code],
    };
  }

  _log(...params) {
    console.log(
      '[' +
        this.className +
        '] ' +
        (typeof params[0] === 'object'
          ? this.jsonStringify(params[0])
          : params[0]),
      ...params.slice(1)
    );
  }

  _logError({ error, method }) {
    console.error('[' + this.className + '] ' + method + ' | ' + error.message);
  }

  _logWarn({ error, method }) {
    console.warn('[' + this.className + '] ' + method + ' | ' + error.message);
  }

  _processResponseData(response: unknown, method: string) {
    if (method) {
      // console.log(method);
    }

    return response;
  }

  _getMergeData(data: unknown, field: string) {
    if (field) {
      //
    }

    return data;
  }
}
